import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { ChartBarIcon, InformationCircleIcon, UsersIcon, EyeIcon, ClockIcon } from '@heroicons/react/outline'
import { IoCalendar } from 'react-icons/io5'
import { DateRangePicker } from 'react-date-range'
import { enUS, arSA } from 'date-fns/locale'
import { defaultStaticRanges, defaultInputRanges } from 'react-date-range/dist/defaultRanges'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import Layout from 'containers/Layout'
import PageContent from 'components/templates/PageContent'
import Alert from 'components/atoms/Alert'
import Popover from 'components/molecules/Popover'
import apiGet from 'lib/network/apiGet'

const PRIMARY = '#fe5579'
const PREVIEW_ROWS = 5

const DEFAULT_DAYS = 30

const formatNumber = value => {
  const n = Number(value) || 0
  return n.toLocaleString()
}

const formatPct = value => {
  if (value == null || Number.isNaN(value)) return '—'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}%`
}

const calcChangePct = (current, previous) => {
  const curr = Number(current) || 0
  const prev = Number(previous) || 0
  if (prev === 0) return curr === 0 ? 0 : 100
  return ((curr - prev) / prev) * 100
}

const createRangeFromDays = days => {
  const end = moment().startOf('day')
  const start = moment().subtract(days, 'days').startOf('day')
  return { startDate: start, endDate: end }
}

const createPreviousRange = current => {
  const days = current.endDate.diff(current.startDate, 'days') + 1
  const end = moment(current.startDate).subtract(1, 'day').startOf('day')
  const start = moment(end)
    .subtract(days - 1, 'days')
    .startOf('day')
  return { startDate: start, endDate: end }
}

const toSelection = range => ({
  startDate: range.startDate.toDate(),
  endDate: range.endDate.toDate(),
  key: 'selection',
})

const toApiDates = range => ({
  startDate: range.startDate.format('YYYY-MM-DD'),
  endDate: range.endDate.format('YYYY-MM-DD'),
})

const formatRangeLabel = range => `${range.startDate.format('MMM Do, YYYY')} - ${range.endDate.format('MMM Do, YYYY')}`

const DateRangeSelector = ({ label, displayLabel, selection, onChange, datePickerLocale, localizedStaticRanges, localizedInputRanges, t, isRtl, placement = 'bottom' }) => (
  <div className="min-w-0">
    <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
    <Popover
      custom
      placement={placement}
      content={close => (
        <DateRangePicker
          weekStartsOn={0}
          locale={datePickerLocale}
          staticRanges={localizedStaticRanges}
          inputRanges={localizedInputRanges}
          startDatePlaceholder={t('reports.datePicker.early')}
          endDatePlaceholder={t('reports.datePicker.continuous')}
          footerContent={
            <button
              onClick={close}
              type="button"
              className="w-[100px] mt-4 py-2 mr-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
            >
              {t('reports.datePicker.close')}
            </button>
          }
          ranges={[selection]}
          onChange={onChange}
        />
      )}
    >
      <button
        type="button"
        className="inline-flex h-10 min-w-[210px] max-w-[260px] w-full items-center border border-gray-300 shadow-sm text-sm leading-4 font-regular rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none transition-all px-3"
      >
        <IoCalendar className={classNames('h-4 w-4 shrink-0 text-gray-500', isRtl ? 'ml-2' : 'mr-2')} />
        <span className="truncate">{displayLabel}</span>
      </button>
    </Popover>
  </div>
)

const Sparkline = ({ color = PRIMARY }) => (
  <svg viewBox="0 0 160 48" className="h-12 w-full" preserveAspectRatio="none" aria-hidden>
    <defs>
      <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.28" />
        <stop offset="100%" stopColor={color} stopOpacity="0.02" />
      </linearGradient>
    </defs>
    <path d="M0 34 C18 32, 28 22, 42 24 S68 38, 84 20 S118 10, 140 16 L160 18 L160 48 L0 48 Z" fill={`url(#spark-${color.replace('#', '')})`} />
    <path d="M0 34 C18 32, 28 22, 42 24 S68 38, 84 20 S118 10, 140 16 L160 18" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)

const MetricCard = ({ title, value, previous, compare, color, icon: Icon }) => {
  // eslint-disable-next-line
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '')) || 0
  const change = compare ? calcChangePct(numericValue, previous) : null
  const up = change != null && change >= 0
  // eslint-disable-next-line
  const displayValue = typeof value === 'number' ? formatNumber(value) : value

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(15,23,42,0.1)]"
      style={{ backgroundImage: `linear-gradient(145deg, ${color}12 0%, #ffffff 42%, #ffffff 100%)` }}
    >
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-20" style={{ backgroundColor: color }} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{title}</p>
          <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900">{displayValue}</p>
        </div>
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-sm ring-1 ring-inset ring-white/60"
          style={{ backgroundColor: `${color}22`, color }}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <div className="relative mt-4">
        <Sparkline color={color} />
      </div>
      {compare ? (
        <p className={classNames('relative mt-3 text-xs font-semibold', up ? 'text-emerald-600' : 'text-rose-600')}>
          {up ? '▲' : '▼'} {formatPct(change)} <span className="font-medium text-slate-400">vs {formatNumber(previous)}</span>
        </p>
      ) : (
        <div className="relative mt-3 h-4" />
      )}
    </div>
  )
}

const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16']

const HorizontalBarChart = ({ rows, compare, color, prevColor }) => {
  const TOP = 8
  const displayed = rows.slice(0, 12)
  const maxVal = Math.max(...displayed.map(r => Math.max(Number(r.eventCount) || 0, compare ? Number(r.prevEventCount) || 0 : 0)), 1)
  const rowH = compare ? 32 : 24
  const gap = 8
  const labelW = 120
  const barAreaW = 260
  const width = labelW + barAreaW + 16
  const height = TOP + displayed.length * (rowH + gap) + 8

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: Math.min(height, 400) }}>
      {displayed.map((row, i) => {
        const y = TOP + i * (rowH + gap)
        const currW = Math.round(((Number(row.eventCount) || 0) / maxVal) * barAreaW)
        const prevW = compare ? Math.round(((Number(row.prevEventCount) || 0) / maxVal) * barAreaW) : 0
        const barH = compare ? 12 : 14
        const label = String(row.value ?? '').slice(0, 18) + (String(row.value ?? '').length > 18 ? '…' : '')
        return (
          <g key={row.value ?? i}>
            <text x={labelW - 6} y={y + barH - 1} textAnchor="end" fontSize="10" fill="#64748b" fontFamily="ui-monospace,monospace">
              {label}
            </text>
            <rect x={labelW} y={y} width={currW} height={barH} rx="3" fill={color} opacity="0.85" />
            <text x={labelW + currW + 4} y={y + barH - 1} fontSize="9" fill="#334155" fontWeight="600">
              {formatNumber(row.eventCount)}
            </text>
            {compare && (
              <>
                <rect x={labelW} y={y + barH + 3} width={prevW} height={barH} rx="3" fill={prevColor} opacity="0.5" />
                <text x={labelW + prevW + 4} y={y + barH * 2 + 2} fontSize="9" fill="#94a3b8">
                  {formatNumber(row.prevEventCount)}
                </text>
              </>
            )}
          </g>
        )
      })}
    </svg>
  )
}

const TrendCard = ({ name, rows, compare, color, prevColor, t }) => {
  const [metric, setMetric] = useState('eventCount')
  const sorted = useMemo(() => [...rows].sort((a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0)), [rows, metric])

  if (!rows.length) return null

  const displayRows = sorted.map(r => ({
    value: r.value,
    eventCount: r[metric] ?? r.eventCount,
    // eslint-disable-next-line
    prevEventCount: r[`prev${metric.charAt(0).toUpperCase() + metric.slice(1)}`] ?? r.prevEventCount,
  }))

  return (
    <div className="flex flex-col rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h3 className="truncate font-mono text-sm font-semibold text-slate-800">{name}</h3>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5 text-xs">
          {['eventCount', 'totalUsers'].map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setMetric(m)}
              className={classNames('rounded-md px-2.5 py-1 font-medium transition', metric === m ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
            >
              {t(`googleAnalytics.metrics.${m}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="p-4">
        {compare && (
          <div className="mb-3 flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
              {t('googleAnalytics.currentPeriod')}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: prevColor, opacity: 0.5 }} />
              {t('googleAnalytics.comparisonPeriod')}
            </span>
          </div>
        )}
        <HorizontalBarChart rows={displayRows} compare={compare} color={color} prevColor={prevColor} />
        {rows.length > 12 && <p className="mt-2 text-center text-xs text-slate-400">{t('googleAnalytics.topN', { n: 12 })}</p>}
      </div>
    </div>
  )
}

const ParameterCard = ({ name, rows, compare, t }) => {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? rows : rows.slice(0, PREVIEW_ROWS)

  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h3 className="truncate font-mono text-sm font-semibold text-slate-800">{name}</h3>
        {rows.length > PREVIEW_ROWS && (
          <button type="button" onClick={() => setExpanded(v => !v)} className="text-xs font-medium text-dark-600 hover:text-dark-800">
            {expanded ? t('googleAnalytics.hide') : t('googleAnalytics.viewAll')}
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">{t('googleAnalytics.value')}</th>
              <th className="px-4 py-2 font-medium">{t('googleAnalytics.eventCount')}</th>
              <th className="px-4 py-2 font-medium">{t('googleAnalytics.totalUsers')}</th>
              {compare && (
                <>
                  <th className="px-4 py-2 font-medium">{t('googleAnalytics.prevEventCount')}</th>
                  <th className="px-4 py-2 font-medium">{t('googleAnalytics.prevTotalUsers')}</th>
                  <th className="px-4 py-2 font-medium">{t('googleAnalytics.change')}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map(row => {
              const change = compare ? calcChangePct(row.eventCount, row.prevEventCount) : null
              const up = change != null && change >= 0
              return (
                <tr key={`${name}-${row.value}`} className="hover:bg-slate-50/80">
                  <td className="max-w-[14rem] truncate px-4 py-2.5 text-slate-800" title={row.value}>
                    {row.value}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-slate-700">{formatNumber(row.eventCount)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-slate-700">{formatNumber(row.totalUsers)}</td>
                  {compare && (
                    <>
                      <td className="px-4 py-2.5 tabular-nums text-slate-500">{formatNumber(row.prevEventCount)}</td>
                      <td className="px-4 py-2.5 tabular-nums text-slate-500">{formatNumber(row.prevTotalUsers)}</td>
                      <td className={classNames('px-4 py-2.5 text-xs font-semibold tabular-nums', up ? 'text-emerald-600' : 'text-rose-600')}>
                        {up ? '▲' : '▼'} {formatPct(Math.abs(change))}
                      </td>
                    </>
                  )}
                </tr>
              )
            })}
            {!rows.length && (
              <tr>
                <td colSpan={compare ? 6 : 3} className="px-4 py-6 text-center text-slate-400">
                  {t('googleAnalytics.noParameterData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {rows.length > PREVIEW_ROWS && (
        <button type="button" onClick={() => setExpanded(true)} className="border-t border-slate-100 px-4 py-2.5 text-left text-xs font-medium text-dark-600 hover:bg-slate-50">
          {t('googleAnalytics.viewAllParam', { name })} →
        </button>
      )}
    </div>
  )
}

const GoogleAnalytics = () => {
  const { t, i18n } = useTranslation()
  const isRtl = i18n.language === 'ar'
  const datePickerLocale = useMemo(() => (isRtl ? arSA : enUS), [isRtl])

  const localizedStaticRanges = useMemo(() => {
    const labels = {
      Today: t('reports.datePicker.today'),
      Yesterday: t('reports.datePicker.yesterday'),
      'This Week': t('reports.datePicker.thisWeek'),
      'Last Week': t('reports.datePicker.lastWeek'),
      'This Month': t('reports.datePicker.thisMonth'),
      'Last Month': t('reports.datePicker.lastMonth'),
    }
    return defaultStaticRanges.map(range => ({
      ...range,
      label: labels[range.label] || range.label,
    }))
  }, [t])

  const localizedInputRanges = useMemo(() => {
    return defaultInputRanges.map(range => {
      if (range.label === 'days up to today') {
        return { ...range, label: t('reports.datePicker.continuous') }
      }
      if (range.label === 'days starting today') {
        return { ...range, label: t('reports.datePicker.early') }
      }
      return range
    })
  }, [t])

  const defaultCurrentRange = useMemo(() => createRangeFromDays(DEFAULT_DAYS), [])
  const defaultCompareRange = useMemo(() => createPreviousRange(defaultCurrentRange), [defaultCurrentRange])

  const [comparePreset, setComparePreset] = useState('previous')
  const [compare, setCompare] = useState(false)
  const [currentRange, setCurrentRange] = useState(defaultCurrentRange)
  const [compareRange, setCompareRange] = useState(defaultCompareRange)
  const [currentSelection, setCurrentSelection] = useState(() => toSelection(defaultCurrentRange))
  const [compareSelection, setCompareSelection] = useState(() => toSelection(defaultCompareRange))
  const [innerTab, setInnerTab] = useState('parameters')
  const [events, setEvents] = useState([])
  const [prevEvents, setPrevEvents] = useState([])
  const [selectedEvent, setSelectedEvent] = useState('')
  const [parameterReport, setParameterReport] = useState(null)
  const [prevParameterReport, setPrevParameterReport] = useState(null)
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [loadingParams, setLoadingParams] = useState(false)
  const [error, setError] = useState('')

  const apiCurrent = useMemo(() => toApiDates(currentRange), [currentRange])
  const apiCompare = useMemo(() => toApiDates(compareRange), [compareRange])

  const handleCurrentDateChange = ranges => {
    const selection = ranges?.selection
    if (!selection?.startDate || !selection?.endDate) return

    const nextCurrent = {
      startDate: moment(selection.startDate).startOf('day'),
      endDate: moment(selection.endDate).startOf('day'),
    }
    setCurrentRange(nextCurrent)
    setCurrentSelection({ ...selection, key: 'selection' })
    if (comparePreset === 'previous') {
      const nextCompare = createPreviousRange(nextCurrent)
      setCompareRange(nextCompare)
      setCompareSelection(toSelection(nextCompare))
    }
  }

  const handleCompareDateChange = ranges => {
    const selection = ranges?.selection
    if (!selection?.startDate || !selection?.endDate) return

    const nextCompare = {
      startDate: moment(selection.startDate).startOf('day'),
      endDate: moment(selection.endDate).startOf('day'),
    }
    setComparePreset('custom')
    setCompareRange(nextCompare)
    setCompareSelection({ ...selection, key: 'selection' })
  }

  const currentDisplayLabel = useMemo(() => formatRangeLabel(currentRange), [currentRange])
  const compareDisplayLabel = useMemo(() => formatRangeLabel(compareRange), [compareRange])

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true)
    setError('')
    try {
      const [currentRes, previousRes] = await Promise.all([
        apiGet('analytics/ga4/events', apiCurrent),
        compare ? apiGet('analytics/ga4/events', apiCompare) : Promise.resolve(null),
      ])

      const list = currentRes?.data?.events || []
      setEvents(list)
      setPrevEvents(previousRes?.data?.events || [])
      setSelectedEvent(prev => {
        // eslint-disable-next-line
        if (prev && list.some(e => e.eventName === prev)) return prev
        return list[0]?.eventName || ''
      })
    } catch (err) {
      setError(err?.message || t('googleAnalytics.loadError'))
      setEvents([])
      setPrevEvents([])
    } finally {
      setLoadingEvents(false)
    }
  }, [apiCompare.endDate, apiCompare.startDate, apiCurrent.endDate, apiCurrent.startDate, compare, t])

  const loadParameters = useCallback(async () => {
    if (!selectedEvent) {
      setParameterReport(null)
      setPrevParameterReport(null)
      return
    }
    setLoadingParams(true)
    setError('')
    try {
      const [currentRes, previousRes] = await Promise.all([
        apiGet(`analytics/ga4/events/${encodeURIComponent(selectedEvent)}/parameters`, apiCurrent),
        compare ? apiGet(`analytics/ga4/events/${encodeURIComponent(selectedEvent)}/parameters`, apiCompare) : Promise.resolve(null),
      ])
      setParameterReport(currentRes?.data || null)
      setPrevParameterReport(previousRes?.data || null)
    } catch (err) {
      setError(err?.message || t('googleAnalytics.loadError'))
      setParameterReport(null)
      setPrevParameterReport(null)
    } finally {
      setLoadingParams(false)
    }
  }, [apiCompare.endDate, apiCompare.startDate, apiCurrent.endDate, apiCurrent.startDate, compare, selectedEvent, t])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  useEffect(() => {
    loadParameters()
  }, [loadParameters])
  // eslint-disable-next-line
  const selectedEventStats = useMemo(() => events.find(e => e.eventName === selectedEvent) || { eventCount: 0, totalUsers: 0 }, [events, selectedEvent])
  // eslint-disable-next-line
  const prevEventStats = useMemo(() => prevEvents.find(e => e.eventName === selectedEvent) || { eventCount: 0, totalUsers: 0 }, [prevEvents, selectedEvent])

  const viewsPerUser = selectedEventStats.totalUsers > 0 ? selectedEventStats.eventCount / selectedEventStats.totalUsers : 0
  const prevViewsPerUser = prevEventStats.totalUsers > 0 ? prevEventStats.eventCount / prevEventStats.totalUsers : 0

  const parameterCards = useMemo(() => {
    const currentParams = parameterReport?.parameters || {}
    const previousParams = prevParameterReport?.parameters || {}
    // eslint-disable-next-line
    const keys = parameterReport?.parameterKeys?.length ? parameterReport.parameterKeys : Object.keys(currentParams)

    return keys.map(key => {
      const currentRows = currentParams[key] || []
      const prevByValue = new Map((previousParams[key] || []).map(r => [r.value, r]))
      const rows = currentRows.map(row => ({
        value: row.value,
        eventCount: row.eventCount,
        totalUsers: row.totalUsers,
        prevEventCount: prevByValue.get(row.value)?.eventCount || 0,
        prevTotalUsers: prevByValue.get(row.value)?.totalUsers || 0,
      }))
      return { name: key, rows }
    })
  }, [parameterReport, prevParameterReport])

  return (
    <Layout title={t('googleAnalytics.title')} hideHeader>
      <PageContent>
        <div className={classNames('space-y-6 px-5 py-6 sm:px-8 lg:px-10', isRtl && 'text-right')} dir={isRtl ? 'rtl' : 'ltr'}>
          <div className="flex flex-col gap-5 rounded-2xl border border-slate-200/70 bg-gradient-to-br from-white via-slate-50/80 to-rose-50/40 p-5 shadow-[0_8px_24px_rgba(15,23,42,0.05)] sm:p-6">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-[1.75rem]">{t('googleAnalytics.title')}</h1>
                  <InformationCircleIcon className="h-5 w-5 shrink-0 text-slate-400" title={t('googleAnalytics.subtitle')} />
                </div>
                <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-slate-500">{t('googleAnalytics.subtitle')}</p>
                {parameterReport?.propertyId && (
                  <p className="mt-2 font-mono text-xs text-slate-400">
                    {t('googleAnalytics.propertyId')}: {parameterReport.propertyId}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <DateRangeSelector
                  label={t('googleAnalytics.dateRange')}
                  displayLabel={currentDisplayLabel}
                  selection={currentSelection}
                  onChange={handleCurrentDateChange}
                  datePickerLocale={datePickerLocale}
                  localizedStaticRanges={localizedStaticRanges}
                  localizedInputRanges={localizedInputRanges}
                  t={t}
                  isRtl={isRtl}
                  placement={isRtl ? 'left' : 'right'}
                />

                {compare && (
                  <>
                    <span className="mb-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">vs</span>
                    <DateRangeSelector
                      label={t('googleAnalytics.compareTo')}
                      displayLabel={compareDisplayLabel}
                      selection={compareSelection}
                      onChange={handleCompareDateChange}
                      datePickerLocale={datePickerLocale}
                      localizedStaticRanges={localizedStaticRanges}
                      localizedInputRanges={localizedInputRanges}
                      t={t}
                      isRtl={isRtl}
                      placement={isRtl ? 'left' : 'right'}
                    />
                  </>
                )}

                <label className="mb-0.5 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
                  <span className="font-medium">{t('googleAnalytics.compare')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={compare}
                    onClick={() => setCompare(v => !v)}
                    className={classNames('relative h-6 w-11 rounded-full transition', compare ? 'bg-dark-600' : 'bg-slate-300')}
                  >
                    <span className={classNames('absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition', compare ? 'left-5' : 'left-0.5')} />
                  </button>
                </label>
              </div>
            </div>
          </div>

          {error ? <Alert type="error" message={error} showIcon /> : null}

          <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white px-1 pt-1 shadow-sm">
            <div className="flex min-w-max gap-1 border-b border-slate-100 px-2 pt-2">
              {loadingEvents && !events.length ? (
                <div className="px-4 py-3 text-sm text-slate-400">{t('googleAnalytics.loadingEvents')}</div>
              ) : (
                events.map(event => {
                  const active = event.eventName === selectedEvent
                  return (
                    <button
                      key={event.eventName}
                      type="button"
                      onClick={() => setSelectedEvent(event.eventName)}
                      className={classNames(
                        'relative whitespace-nowrap rounded-t-xl px-4 py-2.5 text-sm font-medium transition',
                        active ? 'bg-slate-50 text-dark-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                      )}
                    >
                      <span className="font-mono">{event.eventName}</span>
                      <span className="ml-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] tabular-nums text-slate-500">{formatNumber(event.eventCount)}</span>
                      {active && <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full" style={{ backgroundColor: PRIMARY }} />}
                    </button>
                  )
                })
              )}
              {!loadingEvents && !events.length && <div className="px-4 py-3 text-sm text-slate-400">{t('googleAnalytics.noEvents')}</div>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title={t('googleAnalytics.metrics.eventCount')}
              value={selectedEventStats.eventCount}
              previous={prevEventStats.eventCount}
              compare={compare}
              color="#3b82f6"
              icon={ChartBarIcon}
            />
            <MetricCard
              title={t('googleAnalytics.metrics.totalUsers')}
              value={selectedEventStats.totalUsers}
              previous={prevEventStats.totalUsers}
              compare={compare}
              color="#8b5cf6"
              icon={UsersIcon}
            />
            <MetricCard
              title={t('googleAnalytics.metrics.viewsPerUser')}
              value={viewsPerUser.toFixed(2)}
              previous={prevViewsPerUser}
              compare={compare}
              color="#10b981"
              icon={EyeIcon}
            />
            <MetricCard
              title={t('googleAnalytics.metrics.uniqueParamValues')}
              value={parameterCards.reduce((sum, card) => sum + card.rows.length, 0)}
              // eslint-disable-next-line
              previous={prevParameterReport ? Object.values(prevParameterReport.parameters || {}).reduce((sum, rows) => sum + (rows?.length || 0), 0) : 0}
              compare={compare}
              color="#f59e0b"
              icon={ClockIcon}
            />
          </div>

          <div className="flex gap-4 border-b border-slate-200">
            {['parameters', 'trend'].map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setInnerTab(tab)}
                className={classNames('relative pb-3 text-sm font-medium transition', innerTab === tab ? 'text-dark-700' : 'text-slate-400 hover:text-slate-600')}
              >
                {t(`googleAnalytics.tabs.${tab}`)}
                {innerTab === tab && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full" style={{ backgroundColor: PRIMARY }} />}
              </button>
            ))}
          </div>

          {innerTab === 'parameters' && (
            <>
              {loadingParams ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center text-sm text-slate-400">{t('googleAnalytics.loadingParameters')}</div>
              ) : (
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                  {parameterCards.map(card => (
                    <ParameterCard key={card.name} name={card.name} rows={card.rows} compare={compare} t={t} />
                  ))}
                  {!parameterCards.length && selectedEvent && (
                    <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center text-sm text-slate-400">
                      {t('googleAnalytics.noParameterData')}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {innerTab === 'trend' && (
            <>
              {loadingParams ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center text-sm text-slate-400">{t('googleAnalytics.loadingParameters')}</div>
              ) : parameterCards.length ? (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  {parameterCards.map((card, idx) => (
                    <TrendCard
                      key={card.name}
                      name={card.name}
                      rows={card.rows}
                      compare={compare}
                      color={CHART_COLORS[idx % CHART_COLORS.length]}
                      prevColor={CHART_COLORS[idx % CHART_COLORS.length]}
                      t={t}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center text-sm text-slate-400">
                  {selectedEvent ? t('googleAnalytics.noParameterData') : t('googleAnalytics.noEvents')}
                </div>
              )}
            </>
          )}

          {parameterReport?.warnings?.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={
                <ul className="list-disc space-y-1 pl-4 text-sm">
                  {parameterReport.warnings.map(warning => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              }
            />
          )}

          <p className="flex items-start gap-2 text-xs text-slate-400">
            {/* <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" /> */}
            {/* {t('googleAnalytics.footerNote')} */}
          </p>
        </div>
      </PageContent>
    </Layout>
  )
}

export default GoogleAnalytics
